import { z } from "zod";
import { timestamp } from "./common.js";

/**
 * Lifecycle of a newsletter subscriber. Signups are single opt-in, so a new
 * email is `active` the moment it's submitted (no confirmation step).
 */
export const subscriberStatus = z.enum(["active", "unsubscribed"]);
export type SubscriberStatus = z.infer<typeof subscriberStatus>;

/** Who unsubscribed: an admin (from the dashboard) or the person themselves. */
export const unsubscribeSource = z.enum(["admin", "self"]);
export type UnsubscribeSource = z.infer<typeof unsubscribeSource>;

/** A newsletter subscriber row (admin-facing shape). */
export const subscriber = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  status: subscriberStatus,
  /** When they first signed up. */
  createdAt: timestamp,
  updatedAt: timestamp,
  /** When they (re)joined the list — equals createdAt for a fresh signup. */
  activatedAt: timestamp.nullable(),
  /** For unsubscribed rows: who did it. null while active. A `self` removal
   *  can't be reversed by an admin — only the person can re-subscribe. */
  unsubscribedBy: unsubscribeSource.nullable(),
});
export type Subscriber = z.infer<typeof subscriber>;

/** Public capture payload. `turnstileToken` is optional and only checked when
 *  Turnstile is configured on the API, so builds/tests pass without it. */
export const subscribeInput = z.object({
  email: z.string().email().max(320),
  turnstileToken: z.string().optional(),
});
export type SubscribeInput = z.infer<typeof subscribeInput>;

/** Response to POST /api/subscribe. Single opt-in, so the subscriber is `active`. */
export const subscribeResponse = z.object({
  status: z.literal("active"),
  message: z.string(),
});
export type SubscribeResponse = z.infer<typeof subscribeResponse>;
